/**
 * Package Link — show view on the shared Inspector grammar (one-grammar law).
 * A package-graph edge (append-only, no edit). Overview leads with the link type
 * and the domain-specific source → target relationship (preserved as a
 * full-width arrow row of `RefLink`s); Relations restate the two endpoints;
 * description renders as markdown when present; the id/timestamp tail sinks to a
 * muted Technical section; Audit closes the page.
 */
import { useShow, type HttpError } from "@refinedev/core";
import { ArrowRight, Boxes } from "@/components/ui/icon";
import type { PackageLink } from "@aos/shared";

import { Badge } from "@/components/ui/badge";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { Markdown } from "@/components/app";
import { formatDateTime, humanize } from "@/lib/format";

export function PackageLinkShow() {
  const { query } = useShow<PackageLink, HttpError>({
    resource: "package-links",
  });
  const { data, isLoading, isError, error, refetch } = query;
  const link = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !link) {
    return (
      <ShowPageError
        message={error?.message ?? "Link not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title="Package Link"
        subtitle="A typed edge between two packages — its direction and meaning."
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Link type">
            <Badge variant="outline">{humanize(link.link_type)}</Badge>
          </Field>
          <Field label="Relationship" full>
            <span className="inline-flex items-center gap-2">
              <RefLink resource="packages" id={link.source_package_id} />
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <RefLink resource="packages" id={link.target_package_id} />
            </span>
          </Field>
        </FieldGrid>
      </InspectorSection>

      {link.description && link.description.trim() ? (
        <InspectorSection title="Body">
          <Markdown source={link.description} />
        </InspectorSection>
      ) : null}

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "source",
                  label: "Source",
                  icon: Boxes,
                  resource: "packages",
                  id: link.source_package_id,
                },
                {
                  key: "target",
                  label: "Target",
                  icon: Boxes,
                  resource: "packages",
                  id: link.target_package_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={link.id} />
          </Field>
          <Field label="Created">{formatDateTime(link.created_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="package-links" entityId={link.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default PackageLinkShow;
